import React, { FC } from "react";
import { Rw1Logo } from "../icons/Rw1Logo";
import { Rw1LogoColored } from "../icons/RW1LogoColored";
import { useIsMobile } from "../hooks/useIsMobile";

import powerButton from "../assets/powerbutton.png";
import strap from "../assets/strap.png";
import volume_spacing from "../assets/volume_spacing.png";
import controller from "../assets/controller.png";
import passcode from "../assets/passcode.png";
import metaButton from "../assets/metabutton.png";
import rw1App from "../assets/rw1app.png";
import appLibrary from "../assets/applibrary.png";
import metaForWork from "../assets/metaforwork.png";
import login from "../assets/login.png";
import homepage from "../assets/homepage.png";
import community from "../assets/community.png";

const colors = {
  pageBg: "#F4F7FC",
  text: "#1B2436",
  darkText: "#10192B",
  mutedText: "#4A5568",
  softText: "#6B7688",
  blue: "#1E6FEB",
  line: "#D7E3F8",
  border: "#E7EDF7",
};

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: colors.pageBg,
    color: colors.text,
    minHeight: "100dvh",
    width: "100dvw",
    overflowY: "auto",
  },
  section: {
    marginTop: "24px",
    width: "900px",
    backgroundColor: "#f2f2f7",
    borderRadius: "24px",
    padding: "18px 9px",
    margin: "0 7"
  },
  textBlock: {
    height: "96px"
  },
  container: {
    fontFamily: "'Inter', sans-serif",
    color: colors.text,
    width: "100%",
    maxWidth: 880,
    margin: "0 auto",
    padding: "0 32px 96px",
  },
  logoWrap: {
    padding: "40px 0 8px",
  },
  hero: {
    background: "linear-gradient(135deg, #DCE9FB 0%, #F0F5FD 100%)",
    borderRadius: 20,
    padding: "48px 44px",
    marginTop: 16,
  },
  badge: {
    display: "inline-block",
    background: colors.blue,
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 1,
    padding: "6px 14px",
    borderRadius: 20,
    marginBottom: 18,
  },
  h1: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 38,
    fontWeight: 700,
    margin: "0 0 14px",
    color: colors.darkText,
  },
  heroText: {
    fontSize: 17,
    lineHeight: 1.6,
    color: colors.mutedText,
    margin: 0,
    maxWidth: 620,
  },
  steps: {
    display: "flex",
    flexDirection: "column",
    marginTop: 40,
  },
  step: {
    display: "flex",
    gap: 24,
  },
  timeline: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
    width: 44,
  },
  circle: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: colors.blue,
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  connector: {
    width: 2,
    flex: 1,
    background: colors.line,
    marginTop: 6,
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "28px 28px 32px",
    marginBottom: 28,
    flex: 1,
    boxShadow: "0 1px 3px rgba(16,25,43,0.06)",
  },
  finalCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "28px 28px 32px",
    flex: 1,
    boxShadow: "0 1px 3px rgba(16,25,43,0.06)",
  },
  h3: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 19,
    fontWeight: 600,
    margin: "0 0 10px",
    color: colors.darkText,
  },
  p: {
    fontSize: 15.5,
    lineHeight: 1.65,
    color: colors.mutedText,
    margin: 0,
  },
  imageRow: {
    display: "flex",
    gap: 16,
    alignItems: "center",
    flexWrap: "wrap",
  },
  image: {
    height: "154px",
    width: "252px",
    borderRadius: "24px",
    boxShadow: "0 0 20px rgba(0,0,0,0.1)"
  },
  largeImage: {
    height: "244px",
    width: "400px",
    borderRadius: "24px",
    backgroundColor: "black",
    boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "42px",
    marginBottom: "15px"
  },
  twoCol: {
    display: "flex",
    gap: 20,
    flexWrap: "wrap",
  },
  col: {
    flex: "1",
    minWidth: 220,
    marginBottom: "12px"
  },
  label: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    color: colors.softText,
    marginBottom: 10,
    letterSpacing: 0.3,
  },
  tocItem: {
    display: "inline",
    marginLeft: "8px"
  }
} satisfies Record<string, React.CSSProperties>;

export const NewInstructionsPage: FC = () => {
  const isMobile = useIsMobile();

  return (
    <main style={styles.page}>
      <div style={styles.logoWrap}>
        {isMobile ? (
          <Rw1Logo width="20dvw" />
        ) : (
          <Rw1LogoColored color="text.1" height="5dvw" />
        )}
      </div>
      <h1 style={styles.h1}>
        How to set up your headset and get started with the VR-enabled solution
      </h1>
      {/* <div>
        <a style={styles.tocItem} href="#PowerOn">1. Power on and fit your headset</a>
        <span style={styles.tocItem}>&bull;</span>
        <a style={styles.tocItem} href="#Launch">2. Launch the realworld one App</a>
        <span style={styles.tocItem}>&bull;</span>
        <a style={styles.tocItem} href="#Start">3. Start the VR-enabled solution</a>
      </div> */}
      <section style={styles.section}>
        <h2 id="PowerOn">
          1 • Power on and fit your headset
        </h2>

        <div style={styles.row}>
          <img alt="Headset power button" src={powerButton} style={styles.image} />
          <p style={{ fontSize: "14px" }}>
            <strong>Turn on the headset</strong> by pressing the power button.
          </p>
        </div>

        <div style={styles.row}>
          <img alt="Headset Elite Strap" src={strap} style={styles.image} />
          <div>
            <p style={{ fontSize: "14px" }}>
              <strong>Put on the headset</strong> and adjust the fit:
            </p>
            <ul className="mt-1 list-disc pl-5">
              <li style={{ fontSize: "14px" }}>Use the wheel on the back of the Elite Strap to tighten it.</li>
              <li style={{ fontSize: "14px" }}>Adjust the top strap for a secure and comfortable fit.</li>
            </ul>
          </div>
        </div>

        <div style={styles.row}>
          < img src={volume_spacing} alt="Headset lens spacing" style={styles.image} />
          <div>
            <p style={{ fontSize: "14px" }}>
              <strong>Adjust the lenses and volume</strong> for the best user experience:
            </p>
            <ul className="mt-1 list-disc pl-5">
              <li style={{ fontSize: "14px" }}>
                <strong>Meta Quest 3:</strong> Turn the lens spacing wheel until the image is clear.
              </li>
              <li style={{ fontSize: "14px" }}>
                <strong>Meta Quest 3S / Meta Quest 2:</strong> Gently push the lenses into one of the three preset positions until the image is sharp.
              </li>
            </ul>
          </div>
        </div >

        <div style={styles.row}>
          < img src={controller} alt="Meta Quest controllers" style={styles.image} />
          <div>
            <p style={{ fontSize: "14px" }}>
              <strong>Set up using the controllers:</strong>
            </p>
            <p style={{ fontSize: "14px" }}>
              Use your VR controllers to navigate the menu. Follow the on-screen instructions to calibrate the headset and define a safe play area.
            </p>
          </div>
        </div >
      </section >

      <section style={styles.section}>
        <h2 id="Launch">
          2 • Launch the realworld one App
        </h2>

        <p style={{ fontSize: "14px" }}>
          Depending on the configuration, you might see one of the following flows.
        </p>

        <div style={styles.twoCol}>
          <div style={styles.col}>
            <h3>
              Shared Mode
            </h3>

            <div style={styles.textBlock}>
              <p style={{ fontSize: "14px" }}>
                You may be asked to set a passcode. If a session is already active and you do not know the passcode, end the session and set a new one.
              </p>
            </div>
            <img src={passcode} alt="Passcode screen" style={styles.largeImage} />


            <div style={styles.textBlock}>
              <p style={{ fontSize: "14px" }}>
                Start <strong>realworld one</strong> from the menu in front of you.
              </p>

              <p style={{ fontSize: "14px" }}>
                Click the <strong>Meta button</strong> <img alt="Meta Button" src={metaButton} style={{ width: "16px", height: "16px" }} /> if you cannot see the menu bar.
              </p>
            </div>
            <div style={styles.largeImage}>
              <img src={rw1App} alt="realworld one app" style={styles.largeImage} />
            </div>
          </div>

          <div style={styles.col}>
            <h3>
              Individual Mode
            </h3>

            <div style={styles.textBlock}>
              <p style={{ fontSize: "14px" }}>
                Open the <strong>App library</strong> on the right side of the menu
                bar.
              </p>

              <p style={{ fontSize: "14px" }}>
                Click the <strong>Meta button</strong> <img alt="Meta Button" src={metaButton} style={{ width: "16px", height: "16px" }} /> if you cannot see the menu bar.
              </p>
            </div>

            <img src={appLibrary} alt="Meta Quest app library" style={styles.largeImage} />

            <div style={styles.textBlock}>
              <p style={{ fontSize: "14px" }}>
                Select <strong>For Work</strong> from the menu on the left, then start <strong>realworld one</strong>.
              </p>
            </div>

            <img src={metaForWork} alt="For Work menu" style={styles.largeImage} />
          </div>
        </div>

        <p style={{ fontSize: "14px" }}>
          Follow the on-screen login instructions. Open <a href="https://rwone.com/login" target="_blank" rel="noreferrer">rwone.com/login</a> on your computer or phone and enter the device code.
        </p>

        <div style={{ justifySelf: "center" }}>
          <img src={login} alt="realworld one login screen" style={styles.largeImage} />
        </div>
      </section>

      <section style={styles.section}>
        <h2 id="Start">
          3 • Start the VR-enabled solution
        </h2>

        <p style={{ fontSize: "14px" }}>
          Start an installed solution from the <strong>Home</strong> or <strong>Community</strong> tab.
        </p>

        <div style={styles.twoCol}>
          <div style={styles.col}>
            <div style={styles.textBlock}>
              <p style={{ fontSize: "14px" }}>
                On the <strong>Home</strong> tab, use <strong>Spotlights, Activity,</strong> or <strong>Recent</strong> to join sessions or open featured and recently used content.
              </p>
            </div>
            <img src={homepage} alt="Home tab" style={styles.largeImage} />
          </div>

          <div style={styles.col}>
            <div style={styles.textBlock}>
              <p style={{ fontSize: "14px" }}>
                Open the <strong>Community</strong> tab to see all solutions installed on the device.
              </p>
            </div>
            <img src={community} alt="Community tab" style={styles.largeImage} />
          </div>
        </div>

        <p style={{ fontSize: "14px" }}>
          Click on the blue <strong>Enter VR</strong> field to start the solution</p>
        <p style={{ fontSize: "14px" }}>
          New to VR? Complete the <strong>First Steps</strong> tutorial before starting another solution.
        </p>

        <p style={{ fontSize: "14px" }}>
          <strong>Important:</strong> Allow all permission pop-ups during launch. The app may not start if permissions are declined.
        </p>
      </section>

      <footer style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        width: "100%"
      }}>
        <span style={{ padding: "20px" }}>
          <a href="https://realworld-one.com/imprint/" style={{
            textDecoration: "none",
            color: "#ababab"
          }}>Imprint</a>
        </span>
        <span style={{ padding: "20px" }}>
          <a href="https://realworld-one.com/privacy-notice/" style={{
            textDecoration: "none",
            color: "#ababab"
          }}> Privacy Notice</a>
        </span>
        <span style={{
          color: "#ababab",
          padding: "20px"
        }}>© 2026 realworld one.</span>
      </footer>
    </main >
  );
};